import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

interface PublicChromeProps {
  children: React.ReactNode;
}

interface SuperNavItem {
  _id: string;
  navLabel: string;
  navUrl: string;
}

const sameOriginPath = (url: string): string | null => {
  if (!url) return null;
  if (url.startsWith('/')) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

const PublicChrome: React.FC<PublicChromeProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [superNav, setSuperNav] = useState<SuperNavItem[]>([]);
  const [activeLetter, setActiveLetter] = useState(0);
  const location = useLocation();
  const onServices = location.pathname.startsWith('/services');
  const close = () => setMobileOpen(false);

  useEffect(() => {
    const load = async () => {
      try {
        const api = process.env.REACT_APP_API_URL || '';
        const res = await axios.get(`${api}/api/labs?superFeatured=true&limit=20`);
        const labs = Array.isArray(res.data)
          ? res.data
          : res.data?.data || [];
        const items = (Array.isArray(labs) ? labs : [])
          .filter(
            (lab: any) =>
              lab.superFeatured === true &&
              typeof lab.navLabel === 'string' &&
              lab.navLabel.trim() &&
              typeof lab.navUrl === 'string' &&
              lab.navUrl.trim(),
          )
          .map((lab: any) => ({
            _id: lab._id,
            navLabel: lab.navLabel.trim(),
            navUrl: lab.navUrl.trim(),
          }));
        setSuperNav(items);
        setActiveLetter(0);
      } catch {
        setSuperNav([]);
      }
    };
    load();
  }, []);

  const letterOffsets: number[] = [];
  {
    let acc = 0;
    for (const item of superNav) {
      letterOffsets.push(acc);
      acc += item.navLabel.length;
    }
  }
  const totalLetters = superNav.reduce((n, item) => n + item.navLabel.length, 0);
  /** Step cursor by 1; two glyphs lit — slightly snappier than before */
  const letterMs = 55;

  useEffect(() => {
    if (totalLetters < 1) return undefined;
    const id = window.setInterval(() => {
      setActiveLetter((i) => (i + 1) % totalLetters);
    }, letterMs);
    return () => window.clearInterval(id);
  }, [totalLetters, letterMs]);

  const navCls =
    'px-3.5 py-2 text-[0.75rem] font-semibold tracking-[0.1em] uppercase text-[var(--dusk-muted)] no-underline hover:text-[var(--dusk-ink)] transition-colors';

  const superNavCls = `${navCls} dusk-nav-letters`;

  const renderLetterLabel = (label: string, startIndex: number) =>
    label.split('').map((ch, i) => {
      const globalIndex = startIndex + i;
      const on =
        ch !== ' ' &&
        (globalIndex === activeLetter ||
          globalIndex === (activeLetter + 1) % Math.max(totalLetters, 1));
      return (
        <span
          key={`${globalIndex}-${ch}`}
          className={`dusk-nav-letter${ch === ' ' ? ' dusk-nav-letter--space' : ''}${on ? ' is-on' : ''}`}
        >
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      );
    });

  const renderNavLink = (item: SuperNavItem, itemIndex: number, onClick?: () => void) => {
    const internal = sameOriginPath(item.navUrl);
    const label = renderLetterLabel(item.navLabel, letterOffsets[itemIndex] ?? 0);
    if (internal) {
      return (
        <Link key={item._id} to={internal} className={superNavCls} onClick={onClick}>
          {label}
        </Link>
      );
    }
    return (
      <a
        key={item._id}
        href={item.navUrl}
        className={superNavCls}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  };

  return (
    <div className="dusk-public">
      <header className="sticky top-0 z-[100] border-b border-[var(--dusk-line)] bg-[rgba(10,9,8,0.85)] backdrop-blur-xl">
        <div className="dusk-container flex items-center justify-between py-3.5">
          <Link to="/" className="flex items-center no-underline" onClick={close}>
            <img src={`/images/logo.png?t=${Date.now()}`} alt="DUSKPROOF" className="h-8 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-0.5 rounded-full border border-[var(--dusk-line)] bg-[rgba(18,16,14,0.7)] px-1.5 py-1">
            <Link to="/stake" className={navCls}>
              Stake
            </Link>
            <Link to="/blog" className={navCls}>
              Blog
            </Link>
            {superNav.map((item, i) => renderNavLink(item, i))}
            <Link
              to="/services"
              className={`ml-1 dusk-btn-primary !py-2 !px-4 ${onServices ? '!bg-[var(--dusk-amber)]' : ''}`}
            >
              Services
            </Link>
          </nav>

          <button
            type="button"
            className="md:hidden p-2 text-[var(--dusk-ink)] bg-transparent border-0 cursor-pointer"
            aria-label="Menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-[var(--dusk-line)] bg-[var(--dusk-bg)] px-5 py-4 flex flex-col gap-1">
            <Link
              to="/stake"
              onClick={close}
              className="py-3 text-sm font-semibold tracking-wide uppercase text-[var(--dusk-ink)] no-underline"
            >
              Stake
            </Link>
            <Link
              to="/blog"
              onClick={close}
              className="py-3 text-sm font-semibold tracking-wide uppercase text-[var(--dusk-ink)] no-underline"
            >
              Blog
            </Link>
            {superNav.map((item, i) => renderNavLink(item, i, close))}
            <Link to="/services" onClick={close} className="dusk-btn-primary mt-2 text-center">
              Services
            </Link>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-[var(--dusk-line)] mt-auto">
        <div className="dusk-container py-14 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <img src={`/images/logo.png?t=${Date.now()}`} alt="DUSKPROOF" className="h-8 w-auto mb-5 opacity-90" />
            <p className="text-sm text-[var(--dusk-muted)] max-w-xs leading-relaxed m-0">
              Institution-grade validator infrastructure for reliable passive yield.
            </p>
          </div>
          <div>
            <h4 className="text-[0.68rem] font-semibold tracking-[0.16em] uppercase text-[var(--dusk-faint)] mb-4 mt-0">
              Contacts
            </h4>
            <a
              href="mailto:office@duskproof.com"
              className="block text-sm text-[var(--dusk-ink)] no-underline mb-2 hover:text-[var(--dusk-copper)]"
            >
              office@duskproof.com
            </a>
            <a
              href="mailto:partners@duskproof.com"
              className="block text-sm text-[var(--dusk-ink)] no-underline hover:text-[var(--dusk-copper)]"
            >
              partners@duskproof.com
            </a>
          </div>
          <div>
            <h4 className="text-[0.68rem] font-semibold tracking-[0.16em] uppercase text-[var(--dusk-faint)] mb-4 mt-0">
              Company
            </h4>
            <Link
              to="/brand-kit"
              className="block text-sm text-[var(--dusk-ink)] no-underline mb-2 hover:text-[var(--dusk-copper)]"
            >
              Brand Kit
            </Link>
            <Link
              to="/terms-of-use"
              className="block text-sm text-[var(--dusk-ink)] no-underline mb-2 hover:text-[var(--dusk-copper)]"
            >
              Terms of Use
            </Link>
            <Link
              to="/privacy-policy"
              className="block text-sm text-[var(--dusk-ink)] no-underline mb-2 hover:text-[var(--dusk-copper)]"
            >
              Privacy Policy
            </Link>
          </div>
          <div>
            <h4 className="text-[0.68rem] font-semibold tracking-[0.16em] uppercase text-[var(--dusk-faint)] mb-4 mt-0">
              Social
            </h4>
            <div className="flex gap-4">
              <a
                href="https://x.com/duskproof"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--dusk-muted)] hover:text-[var(--dusk-copper)]"
                title="X"
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://github.com/duskproof"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--dusk-muted)] hover:text-[var(--dusk-copper)]"
                title="GitHub"
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
            <p className="text-xs text-[var(--dusk-faint)] mt-8 mb-0">© {new Date().getFullYear()} DUSKPROOF</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicChrome;
